/**
 * The market — one counter, three things you can do at it.
 *
 * Phase 2c gave the panel the shape a player expects from a shopkeeper: **Buy**, **Sell**,
 * **Repair**, in that order, across the top. The three faction counters (the Lantern Compact, the
 * Hymnal, the Ferry) did not go away — they are a sub-strip inside Buy, because which counter you
 * are standing at only changes what is on the shelf and what it costs, and that is a *buying*
 * question. Selling and mending are Compact business wherever you are standing.
 *
 * Standing is where faction rep finally pays: a counter's header says what it calls you and what
 * that is worth, and every price on that counter is already the discounted one, with the list
 * price struck through beside it. The server is the authority on all of it — the client sends
 * (counter, itemId, qty) and never a price — so a trade re-reads rather than patching a local
 * number.
 *
 * The same component is what Sabel opens out in the world (`ui/overlay.tsx`); there is exactly
 * one market panel in the client and it is this one.
 */

import type { VNode } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ITEMS,
  MAX_DURABILITY,
  durabilityOf,
  isBroken,
  isDamageable,
  repairAllCost,
  repairCost,
  type FactionId,
  type ItemStack,
} from '@stillsun/shared';
import {
  buyItem,
  fetchMarket,
  insureItem,
  repairAll,
  repairItem,
  sellItem,
  type MarketCounter,
  type MarketStashRow,
  type MarketView,
} from '../../net/economy-api.js';
import { session } from '../../net/session.js';
import { Icon, ItemIcon } from '../icons.js';
import {
  ItemTooltip,
  LEDGER_SILENT,
  Loading,
  Notice,
  errText,
  loadList,
  placeLabel,
  rarityHex,
  rarityName,
  rarityRailHex,
  useLoader,
  useProgress,
  wick,
} from './common.js';
import './vendor.css';

export interface VendorProps {
  nonce: number;
  token: string;
  /** In-world: the panel is inside Sabel's frame and draws its own head rather than the hub's. */
  compact?: boolean;
}

type Deal = 'buy' | 'sell' | 'repair';

const DEALS: readonly { id: Deal; label: string; sub: string }[] = [
  { id: 'buy', label: 'Buy', sub: 'What is on the shelf' },
  { id: 'sell', label: 'Sell', sub: 'What they will weigh' },
  { id: 'repair', label: 'Repair', sub: 'What the Band took off you' },
];

interface Receipt {
  id: number;
  /** Positive when Wick came in, negative when it went out. */
  delta: number;
  line: string;
  /** Compact rep earned by a sale, if any. */
  rep: number;
}

const nameOf = (itemId: string): string => ITEMS[itemId]?.name ?? itemId;

export function Vendor(props: VendorProps): VNode {
  const { token } = props;
  const market = useLoader<MarketView>(
    () => fetchMarket(token),
    `market:${String(props.nonce)}:${token}`,
  );
  /**
   * Repair needs *everything* the player owns, not the sellable Stash: a broken helm is on your
   * head, not on a shelf, and `/api/vendor`'s rows are Stash-only and carry no durability. So the
   * Repair tab reads the inventory seam directly, which returns the equipment locations too.
   */
  const owned = useLoader<ItemStack[]>(
    () => loadList<ItemStack>('/api/stash', token, 'items'),
    `vendorstash:${String(props.nonce)}:${token}`,
  );
  const progress = useProgress(token, props.nonce);
  const [deal, setDeal] = useState<Deal>('buy');
  const [counterId, setCounterId] = useState<FactionId>('compact');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [hover, setHover] = useState<{ stack: ItemStack; x: number; y: number } | null>(null);
  const seq = useRef(0);
  const timers = useRef(new Set<number>());

  useEffect(
    () => () => {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current.clear();
    },
    [],
  );

  /** The receipt strip: it slides in, holds long enough to read, and takes itself away. */
  const receipt = (delta: number, line: string, rep = 0): void => {
    seq.current += 1;
    const id = seq.current;
    setReceipts((list) => [...list.slice(-2), { id, delta, line, rep }]);
    const timer = window.setTimeout(() => {
      timers.current.delete(timer);
      setReceipts((list) => list.filter((r) => r.id !== id));
    }, 2600);
    timers.current.add(timer);
  };

  const trade = (run: () => Promise<void>): void => {
    if (busy) return;
    setBusy(true);
    setNote('');
    void (async () => {
      try {
        await run();
        // Stash, shelf and purse all moved, and the server owns all three.
        await session.refresh();
        market.reload();
        owned.reload();
      } catch (err) {
        setNote(errText(err, 'They declined that trade.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  if (market.loading && market.data === null) {
    return (
      <div class={props.compact === true ? 'vn vn-inset' : 'hub-pane vn'}>
        <Loading />
      </div>
    );
  }
  if (market.data === null) {
    return (
      <div class={props.compact === true ? 'vn vn-inset' : 'hub-pane vn'}>
        <Notice>{LEDGER_SILENT}</Notice>
      </div>
    );
  }

  const data = market.data;
  const purse = session.player?.wick ?? data.wick;
  const level = progress.data?.level ?? null;
  const calling = progress.data?.calling ?? null;
  const compactCounter = data.counters.find((c) => c.id === 'compact') ?? null;
  const shown = data.counters.find((c) => c.id === counterId) ?? data.counters[0] ?? null;
  const damaged = (owned.data ?? []).filter((s) => repairCost(s) > 0);

  return (
    <div class={props.compact === true ? 'vn vn-inset' : 'hub-pane vn'}>
      <header class="vn-head">
        <div class="vn-purse">
          <Icon name="wick" size={18} />
          <span class="vn-purse-n">{wick(purse)}</span>
          <span class="vn-purse-l">Wick</span>
        </div>
        <p class="vn-blurb">
          Three counters keep the lamps of Lanternfall lit. They all weigh, and none of them argue.
        </p>
      </header>

      <nav class="vn-deals" role="tablist" aria-label="Counter">
        {DEALS.map((d) => (
          <button
            key={d.id}
            type="button"
            role="tab"
            aria-selected={deal === d.id}
            class={`vn-deal${deal === d.id ? ' on' : ''}`}
            onClick={() => {
              setDeal(d.id);
              setNote('');
            }}
          >
            <span class="vn-deal-name">{d.label}</span>
            <span class="vn-deal-sub">
              {d.id === 'repair' && damaged.length > 0
                ? `${String(damaged.length)} to mend`
                : d.id === 'sell'
                  ? `${String(data.stash.length)} lots`
                  : d.sub}
            </span>
          </button>
        ))}
      </nav>

      {note.length > 0 ? <p class="hub-error">{note}</p> : null}

      {deal === 'buy' ? (
        <>
          <nav class="vn-tabs" role="tablist" aria-label="Which counter">
            {data.counters.map((counter) => (
              <button
                key={counter.id}
                type="button"
                role="tab"
                aria-selected={counterId === counter.id}
                class={`vn-tab${counterId === counter.id ? ' on' : ''}`}
                onClick={() => {
                  setCounterId(counter.id);
                }}
              >
                <span class="vn-tab-name">{counter.name}</span>
                <span class="vn-tab-standing">{counter.standing}</span>
              </button>
            ))}
          </nav>
          {shown === null ? (
            <Notice>Every counter is shuttered. Come back after the next Dimming.</Notice>
          ) : (
            <Counter
              counter={shown}
              purse={purse}
              busy={busy}
              onBuy={(itemId) => {
                trade(async () => {
                  const r = await buyItem(token, shown.id, itemId, 1);
                  receipt(-r.spent, nameOf(r.itemId));
                });
              }}
            />
          )}
        </>
      ) : deal === 'sell' ? (
        <Ledger
          rows={data.stash}
          compact={compactCounter}
          busy={busy}
          onHover={(stack, x, y) => {
            setHover(stack ? { stack, x, y } : null);
          }}
          onSell={(row, qty) => {
            trade(async () => {
              const r = await sellItem(token, row.uid, qty);
              receipt(r.paid, `${nameOf(r.itemId)}${qty > 1 ? ` ×${String(qty)}` : ''}`, r.rep);
            });
          }}
          onInsure={(row) => {
            trade(async () => {
              const r = await insureItem(token, row.uid);
              receipt(-r.premium, `Warrant — ${nameOf(r.itemId)}`);
            });
          }}
        />
      ) : (
        <RepairBench
          damaged={damaged}
          offline={owned.data === null && !owned.loading}
          loading={owned.loading}
          purse={purse}
          busy={busy}
          onHover={(stack, x, y) => {
            setHover(stack ? { stack, x, y } : null);
          }}
          onRepair={(stack) => {
            trade(async () => {
              const r = await repairItem(token, stack.uid);
              receipt(-r.spent, `Mended — ${nameOf(stack.itemId)}`);
            });
          }}
          onRepairAll={() => {
            trade(async () => {
              const r = await repairAll(token);
              receipt(-r.spent, 'Mended — everything');
            });
          }}
        />
      )}

      {hover === null ? null : (
        <ItemTooltip stack={hover.stack} x={hover.x} y={hover.y} level={level} calling={calling} />
      )}

      <div class="vn-receipts" aria-live="polite">
        {receipts.map((r) => (
          <div key={String(r.id)} class={`vn-receipt ${r.delta >= 0 ? 'in' : 'out'}`}>
            <span class="vn-receipt-n">{`${r.delta >= 0 ? '+' : '−'}${wick(Math.abs(r.delta))}`}</span>
            <span class="vn-receipt-l">{r.line}</span>
            {r.rep > 0 ? <span class="vn-receipt-rep">{`+${String(r.rep)} standing`}</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ a counter

function Counter(props: {
  counter: MarketCounter;
  purse: number;
  busy: boolean;
  onBuy: (itemId: string) => void;
}): VNode {
  const { counter } = props;
  const pct = Math.round(counter.discount * 100);
  return (
    <section class="vn-counter">
      <header class="vn-counter-head">
        <div>
          <h2 class="vn-counter-sign">{counter.sign}</h2>
          <p class="vn-counter-blurb">{counter.blurb}</p>
        </div>
        <div class={`vn-standing${pct > 0 ? ' earned' : ''}`}>
          <span class="vn-standing-word">{counter.standing}</span>
          <span class="vn-standing-num">{pct > 0 ? `−${String(pct)}%` : 'list price'}</span>
          <span class="vn-standing-rep">{`${wick(counter.rep)} rep`}</span>
        </div>
      </header>

      {counter.stock.length === 0 ? (
        <Notice>The shelves are bare. Come back after the next Dimming.</Notice>
      ) : (
        <div class="vn-rows">
          {counter.stock.map((line) => {
            const def = ITEMS[line.itemId];
            const afford = props.purse >= line.price;
            const cut = line.price < line.list;
            return (
              <article key={line.itemId} class="vn-row">
                <span
                  class="vn-row-rail"
                  aria-hidden="true"
                  style={{ background: def ? rarityRailHex(def.rarity) : 'var(--rule)' }}
                />
                <ItemIcon def={def} size={30} class="vn-row-ic" />
                <div class="vn-row-body">
                  <span
                    class="vn-row-name"
                    style={{ color: def ? rarityHex(def.rarity) : 'var(--bone)' }}
                  >
                    {def?.name ?? line.itemId}
                    {line.rotating ? <span class="vn-row-flag">Today only</span> : null}
                  </span>
                  <span class="vn-row-desc">
                    {def ? `${rarityName(def.rarity)} · ${def.desc}` : 'Unrecognised by this build'}
                  </span>
                </div>
                <div class="vn-row-price">
                  {cut ? <s class="vn-row-list">{wick(line.list)}</s> : null}
                  <span class={afford ? 'vn-row-now' : 'vn-row-now short'}>{wick(line.price)}</span>
                </div>
                <button
                  type="button"
                  class="vn-btn"
                  disabled={props.busy || !afford}
                  title={afford ? undefined : `${wick(line.price - props.purse)} Wick short`}
                  onClick={() => {
                    props.onBuy(line.itemId);
                  }}
                >
                  {afford ? 'Buy' : 'Too dear'}
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------- selling

function Ledger(props: {
  rows: readonly MarketStashRow[];
  compact: MarketCounter | null;
  busy: boolean;
  onSell: (row: MarketStashRow, qty: number) => void;
  onInsure: (row: MarketStashRow) => void;
  onHover: (stack: ItemStack | null, x: number, y: number) => void;
}): VNode {
  const pct = Math.round((props.compact?.discount ?? 0) * 100);
  // Selling is irreversible, so it takes two presses: the first arms the row, the second sells
  // it. A modal would be heavier than the decision deserves; a button that changes its own word
  // and disarms itself after four seconds is the same safety with none of the ceremony.
  const [armed, setArmed] = useState<string>('');
  const disarm = useRef<number | null>(null);
  const arm = (key: string): void => {
    setArmed(key);
    if (disarm.current !== null) window.clearTimeout(disarm.current);
    disarm.current = window.setTimeout(() => {
      setArmed('');
    }, 4000);
  };
  useEffect(
    () => () => {
      if (disarm.current !== null) window.clearTimeout(disarm.current);
    },
    [],
  );
  const sellPress = (row: MarketStashRow, qty: number): void => {
    const key = `${String(row.uid)}:${String(qty)}`;
    if (armed !== key) {
      arm(key);
      return;
    }
    setArmed('');
    props.onSell(row, qty);
  };
  return (
    <section class="vn-counter">
      <header class="vn-counter-head">
        <div>
          <h2 class="vn-counter-sign">Weighed &amp; Written</h2>
          <p class="vn-counter-blurb">
            They buy out of the stash and nowhere else — move it home first, deliberately, on the
            Stash tab. Warrants are Compact business: insure a lot here and it comes back to you
            after a bad night out.
          </p>
        </div>
        <div class={`vn-standing${pct > 0 ? ' earned' : ''}`}>
          <span class="vn-standing-word">{props.compact?.standing ?? 'Stranger'}</span>
          <span class="vn-standing-num">
            {pct > 0 ? `−${String(pct)}% warrants` : 'full premium'}
          </span>
        </div>
      </header>

      {props.rows.length === 0 ? (
        <Notice>Nothing in the stash to sell. Bring something back, or move it home first.</Notice>
      ) : (
        <div class="vn-rows">
          {props.rows.map((row) => {
            const def = ITEMS[row.itemId];
            const each = row.qty > 1 ? Math.round(row.sell / row.qty) : row.sell;
            const wears = isDamageable(def);
            const dur = durabilityOf(row);
            return (
              <article
                key={String(row.uid)}
                class="vn-row"
                onMouseEnter={(e) => {
                  props.onHover(row, e.clientX, e.clientY);
                }}
                onMouseMove={(e) => {
                  props.onHover(row, e.clientX, e.clientY);
                }}
                onMouseLeave={() => {
                  props.onHover(null, 0, 0);
                }}
              >
                <span
                  class="vn-row-rail"
                  aria-hidden="true"
                  style={{ background: def ? rarityRailHex(def.rarity) : 'var(--rule)' }}
                />
                <ItemIcon
                  def={def}
                  size={30}
                  class="vn-row-ic"
                  {...(wears ? { durability: dur } : {})}
                />
                <div class="vn-row-body">
                  <span
                    class="vn-row-name"
                    style={{ color: def ? rarityHex(def.rarity) : 'var(--bone)' }}
                  >
                    {def?.name ?? row.itemId}
                    {row.qty > 1 ? <span class="vn-row-qty">{` ×${String(row.qty)}`}</span> : null}
                    {row.insured ? <span class="vn-row-flag warrant">Insured</span> : null}
                    {isBroken(row) ? <span class="vn-row-flag broken">Broken</span> : null}
                  </span>
                  <span class="vn-row-desc">
                    {def ? `${rarityName(def.rarity)} · ${wick(each)} Wick each` : 'Unrecognised'}
                  </span>
                </div>
                <div class="vn-row-price">
                  <span class="vn-row-now">{wick(row.sell)}</span>
                </div>
                <div class="vn-row-acts">
                  {!row.insured && row.insurePrice > 0 ? (
                    <button
                      type="button"
                      class="vn-btn quiet"
                      disabled={props.busy}
                      onClick={() => {
                        props.onInsure(row);
                      }}
                      title={`Warrant this lot for ${wick(row.insurePrice)} Wick`}
                    >
                      {`Insure ${wick(row.insurePrice)}`}
                    </button>
                  ) : null}
                  {row.qty > 1 ? (
                    <button
                      type="button"
                      class={`vn-btn quiet${armed === `${String(row.uid)}:1` ? ' armed' : ''}`}
                      disabled={props.busy}
                      onClick={() => {
                        sellPress(row, 1);
                      }}
                    >
                      {armed === `${String(row.uid)}:1` ? 'Sure?' : 'Sell 1'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    class={`vn-btn${armed === `${String(row.uid)}:${String(row.qty)}` ? ' armed' : ''}`}
                    disabled={props.busy}
                    onClick={() => {
                      sellPress(row, row.qty);
                    }}
                  >
                    {armed === `${String(row.uid)}:${String(row.qty)}`
                      ? `Sure? ${wick(row.sell)}`
                      : row.qty > 1
                        ? 'Sell all'
                        : 'Sell'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ the bench

/**
 * Repair. Every damaged thing the player owns — worn or stashed — with what one costs and what
 * the lot costs. Both buttons are disabled *with the shortfall on them* rather than hidden when
 * the purse is short: "480 Wick short" tells a player what to go and do, "nothing here" does not.
 */
function RepairBench(props: {
  damaged: readonly ItemStack[];
  offline: boolean;
  loading: boolean;
  purse: number;
  busy: boolean;
  onRepair: (stack: ItemStack) => void;
  onRepairAll: () => void;
  onHover: (stack: ItemStack | null, x: number, y: number) => void;
}): VNode {
  const total = repairAllCost(props.damaged);
  const canAll = props.purse >= total && total > 0;
  return (
    <section class="vn-counter">
      <header class="vn-counter-head">
        <div>
          <h2 class="vn-counter-sign">Bench &amp; Bellows</h2>
          <p class="vn-counter-blurb">
            Ten points off every piece you are wearing, every time you go down. At nothing left it
            still fits and still shows — it just stops helping. Sabel does not judge.
          </p>
        </div>
        <div class={`vn-standing${total > 0 ? ' earned' : ''}`}>
          <span class="vn-standing-word">{total > 0 ? 'Owing' : 'Sound'}</span>
          <span class="vn-standing-num">
            {total > 0 ? `${wick(total)} Wick` : 'nothing to mend'}
          </span>
        </div>
      </header>

      {props.loading ? (
        <Loading />
      ) : props.offline ? (
        <Notice>{LEDGER_SILENT}</Notice>
      ) : props.damaged.length === 0 ? (
        <Notice>Everything you own is sound. Go and ruin something.</Notice>
      ) : (
        <>
          <div class="vn-repair-all">
            <button
              type="button"
              class="vn-btn"
              disabled={props.busy || !canAll}
              onClick={props.onRepairAll}
            >
              {`Repair all · ${wick(total)} Wick`}
            </button>
            {canAll ? null : (
              <span class="vn-repair-short">{`${wick(total - props.purse)} Wick short of the lot`}</span>
            )}
          </div>
          <div class="vn-rows">
            {props.damaged.map((stack) => {
              const def = ITEMS[stack.itemId];
              const dur = durabilityOf(stack);
              const cost = repairCost(stack);
              const afford = props.purse >= cost;
              return (
                <article
                  key={String(stack.uid)}
                  class="vn-row"
                  onMouseEnter={(e) => {
                    props.onHover(stack, e.clientX, e.clientY);
                  }}
                  onMouseMove={(e) => {
                    props.onHover(stack, e.clientX, e.clientY);
                  }}
                  onMouseLeave={() => {
                    props.onHover(null, 0, 0);
                  }}
                >
                  <span
                    class="vn-row-rail"
                    aria-hidden="true"
                    style={{ background: def ? rarityRailHex(def.rarity) : 'var(--rule)' }}
                  />
                  <ItemIcon def={def} size={30} class="vn-row-ic" durability={dur} />
                  <div class="vn-row-body">
                    <span
                      class="vn-row-name"
                      style={{ color: def ? rarityHex(def.rarity) : 'var(--bone)' }}
                    >
                      {def?.name ?? stack.itemId}
                      {isBroken(stack) ? <span class="vn-row-flag broken">Broken</span> : null}
                    </span>
                    <span class="vn-row-desc">
                      {`${placeLabel(stack)} · ${String(dur)}/${String(MAX_DURABILITY)}`}
                    </span>
                    <span class="vn-wear">
                      <i
                        class={isBroken(stack) ? 'out' : ''}
                        style={{ width: `${String(Math.round((dur / MAX_DURABILITY) * 100))}%` }}
                      />
                    </span>
                  </div>
                  <div class="vn-row-price">
                    <span class={afford ? 'vn-row-now' : 'vn-row-now short'}>{wick(cost)}</span>
                  </div>
                  <button
                    type="button"
                    class="vn-btn quiet"
                    disabled={props.busy || !afford}
                    title={afford ? undefined : `${wick(cost - props.purse)} Wick short`}
                    onClick={() => {
                      props.onRepair(stack);
                    }}
                  >
                    {afford ? 'Mend' : 'Too dear'}
                  </button>
                </article>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
